/*
 * fes-standalone.js
 * Collision-proof SSE client for browsers.
 * No UMD/CommonJS detection — always assigns window.FESStandalone.
 * Returns a HANDLE (not a Promise): { close(), lastEventId }
 */
(function () {
  'use strict';

  function delay(ms, signal) {
    return new Promise((resolve, reject) => {
      const id = setTimeout(resolve, ms);
      if (signal) {
        const onAbort = () => {
          clearTimeout(id);
          reject(Object.assign(new Error('Aborted'), { name: 'AbortError' }));
        };
        if (signal.aborted) onAbort();
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }

  function startEventStream(url, opts = {}) {
    const {
      method = 'GET',
      headers = {},
      body,
      signal,
      onopen,
      onmessage,
      onerror,
      onclose,
      fetch: customFetch,
      retry = 1000,
      maxRetry = 10000,
      lastEventId,
    } = opts;

    const _fetch = (customFetch || window.fetch || globalThis.fetch);
    if (typeof _fetch !== 'function') {
      throw new Error('fetch is not available');
    }

    let attempt = 0;
    let closed = false;
    let currentLastEventId = lastEventId || '';

    const controller = new AbortController();
    if (signal) {
      if (signal.aborted) controller.abort();
      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    async function connect() {
      attempt++;
      const backoff = Math.min(maxRetry, Math.floor(retry * Math.pow(2, attempt - 1)));

      const reqHeaders = new Headers(headers);
      if (!reqHeaders.has('Accept')) reqHeaders.set('Accept', 'text/event-stream');
      if (currentLastEventId && !reqHeaders.has('Last-Event-ID')) {
        reqHeaders.set('Last-Event-ID', currentLastEventId);
      }

      let res;
      try {
        res = await _fetch(url, { method, headers: reqHeaders, body, signal: controller.signal });
      } catch (err) {
        if (controller.signal.aborted) return; // stop quietly
        if (onerror) try { onerror(err); } catch (_) {}
        await delay(backoff, controller.signal).catch(() => {});
        if (!closed) return connect();
        return;
      }

      if (onopen) {
        try {
          await onopen(res);
        } catch (err) {
          if (onerror) try { onerror(err); } catch (_) {}
          controller.abort();
          return;
        }
      }

      const ctype = res.headers.get('content-type') || '';
      if (!/text\/event-stream/i.test(ctype)) {
        const err = new Error(`Expected text/event-stream, got "${ctype}"`);
        if (onerror) try { onerror(err); } catch (_) {}
        await delay(backoff, controller.signal).catch(() => {});
        if (!closed) return connect();
        return;
      }

      const reader = res.body && res.body.getReader && res.body.getReader();
      if (!reader) {
        const err = new Error('ReadableStream not supported on response body');
        if (onerror) try { onerror(err); } catch (_) {}
        await delay(backoff, controller.signal).catch(() => {});
        if (!closed) return connect();
        return;
      }

      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let sseEvent = { data: '', event: 'message', id: '' };

      function dispatchEvent() {
        if (sseEvent.data === '') return;
        const msg = {
          event: sseEvent.event || 'message',
          data: sseEvent.data.replace(/\n$/, ''),
          id: sseEvent.id || undefined,
        };
        if (msg.id) currentLastEventId = msg.id;
        if (onmessage) try { onmessage(msg); } catch (_) {}
        sseEvent = { data: '', event: 'message', id: sseEvent.id };
      }

      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let pos = 0, length = buffer.length;
          while (pos < length) {
            let nl = buffer.indexOf('\n', pos);
            if (nl === -1) break;
            let line = buffer.slice(pos, nl);
            if (line.endsWith('\r')) line = line.slice(0, -1);
            pos = nl + 1;

            if (line === '') { dispatchEvent(); continue; }

            const colon = line.indexOf(':');
            let field = '', valueStr = '';
            if (colon === -1) {
              field = line;
            } else {
              field = line.slice(0, colon);
              valueStr = line.slice(colon + 1);
              if (valueStr.startsWith(' ')) valueStr = valueStr.slice(1);
            }

            switch (field) {
              case 'event': sseEvent.event = valueStr || 'message'; break;
              case 'data': sseEvent.data += valueStr + '\n'; break;
              case 'id': sseEvent.id = valueStr; break;
              case 'retry':
                {
                  const n = parseInt(valueStr, 10);
                  if (!Number.isNaN(n)) { attempt = 1; /* base */ }
                }
                break;
              default: /* ignore */;
            }
          }
          buffer = buffer.slice(pos);
        }

        dispatchEvent();
        if (onclose) try { onclose(); } catch (_) {}
      } catch (err) {
        if (controller.signal.aborted) { if (onclose) try { onclose(); } catch (_) {} return; }
        if (onerror) try { onerror(err); } catch (_) {}
        await delay(backoff, controller.signal).catch(() => {});
        if (!closed) return connect();
      }

      if (!closed) {
        await delay(backoff, controller.signal).catch(() => {});
        return connect();
      }
    }

    connect();

    return {
      close() { closed = true; try { controller.abort(); } catch (_) {} },
      get lastEventId() { return currentLastEventId; }
    };
  }

  window.FESStandalone = { startEventStream };
})();
